import { CustomersHttp } from '../http.ts'
import { Customer, BankAccount, CustomerCompany } from '../types'
import { CompanyOnboardingData } from '../components/customer-companies-management/add-new-customer-company-modal.tsx'
import { CustomerCompanyFeesData } from '../components/customer-companies-management/new-customer-company-form-banking-partners.tsx'

const PREFIX = '/customers'
const headers = {
  'Content-type': 'application/json'
}

export const getCurrentCustomers = (accessToken: string, withoutLimit?: boolean) => {
  const urlToGet = withoutLimit ? `${PREFIX}/customers-data?withoutLimit=true` : `${PREFIX}/customers-data?pageSize=200`

  return CustomersHttp.get(urlToGet, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getCustomerData = (customerId: string, accessToken: string) => {
  return CustomersHttp.get(`${PREFIX}/customer-data/${customerId}`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const createCustomer = (customerToCreateData: Partial<Customer>, accessToken: string) => {
  return CustomersHttp.post(
    `${PREFIX}/create`,
    { ...customerToCreateData },
    {
      headers: {
        ...headers,
        Authorization: `Bearer ${accessToken}`
      }
    }
  )
}

export const deleteCustomerData = (customerId: string, accessToken: string) => {
  return CustomersHttp.put(
    `${PREFIX}/delete-customer/${customerId}`,
    {},
    {
      headers: {
        ...headers,
        Authorization: `Bearer ${accessToken}`
      }
    }
  )
}

export const deleteCustomerCompanyData = (customerCompanyId: string, accessToken: string) => {
  return CustomersHttp.put(
    `${PREFIX}/delete-customer-company/${customerCompanyId}`,
    {},
    {
      headers: {
        ...headers,
        Authorization: `Bearer ${accessToken}`
      }
    }
  )
}

export const resendCustomerCompanyActivationEmail = (payload: object, accessToken: string) => {
  return CustomersHttp.post(`${PREFIX}/resend-activation-email`, payload, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const resendCustomerCompanyOnboardingActivationEmail = (payload: object, accessToken: string) => {
  return CustomersHttp.post(`${PREFIX}/resend-onboarding-activation-email`, payload, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const processCustomerCompanyFunding = (payload: object, accessToken: string) => {
  return CustomersHttp.post(`${PREFIX}/process-customer-company-funding`, payload, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getCustomerDataByRegex = (regexToWork: string, accessToken: string) => {
  return CustomersHttp.post(
    `${PREFIX}/customer-data-by-regex`,
    { regexToWork },
    {
      headers: {
        ...headers,
        Authorization: `Bearer ${accessToken}`
      }
    }
  )
}

export const createBankAccount = (bankAccountData: Partial<BankAccount>, accessToken: string, cutomerId?: string) => {
  return CustomersHttp.put(
    `${PREFIX}/create-bank-account/${cutomerId}`,
    { ...bankAccountData },
    {
      headers: {
        ...headers,
        Authorization: `Bearer ${accessToken}`
      }
    }
  )
}

export const deleteBankAccount = (bankAccountNumber: string, accessToken: string, cutomerId?: string) => {
  return CustomersHttp.put(
    `${PREFIX}/delete-bank-account/${cutomerId}`,
    { bankAccountNumber },
    {
      headers: {
        ...headers,
        Authorization: `Bearer ${accessToken}`
      }
    }
  )
}

export const setBankAccountAsMain = (bankAccountNumber: string, accessToken: string, cutomerId?: string) => {
  return CustomersHttp.put(
    `${PREFIX}/set-bank-account-as-main/${cutomerId}`,
    { bankAccountNumber },
    {
      headers: {
        ...headers,
        Authorization: `Bearer ${accessToken}`
      }
    }
  )
}

export const createCustomerCompany = (customerCompanyData: CustomerCompany, accessToken: string) => {
  return CustomersHttp.post(
    `${PREFIX}/create-customer-company`,
    { ...customerCompanyData },
    {
      headers: {
        ...headers,
        Authorization: `Bearer ${accessToken}`
      }
    }
  )
}

export const customerCompanyOnboarding = (accessToken: string, customerCompanyData?: CompanyOnboardingData, customerCompanyBankingPartnersData?: Array<CustomerCompanyFeesData>) => {
  return CustomersHttp.post(
    `${PREFIX}/customer-company-onboarding`,
    { customerCompanyData, customerCompanyBankingPartnersData },
    {
      headers: {
        ...headers,
        Authorization: `Bearer ${accessToken}`
      }
    }
  )
}

export const updateCustomerCompanyOnboarding = (accessToken: string, customerCompanyId: string, customerCompanyData?: CompanyOnboardingData, customerCompanyBankingPartnersData?: Array<CustomerCompanyFeesData>) => {
  return CustomersHttp.put(
    `${PREFIX}/update-customer-company-onboarding/${customerCompanyId}`,
    { customerCompanyData, customerCompanyBankingPartnersData },
    {
      headers: {
        ...headers,
        Authorization: `Bearer ${accessToken}`
      }
    }
  )
}

export const updateCustomerCompanyPocketData = (pocketId: string, payload: object, accessToken: string) => {
  return CustomersHttp.put(
    `${PREFIX}/update-customer-company-pocket-data/${pocketId}`,
    { ...payload },
    {
      headers: {
        ...headers,
        Authorization: `Bearer ${accessToken}`
      }
    }
  )
}

export const updateCustomerCompanyData = (customerCompanyToUpdateId: string, payload: object, accessToken: string) => {
  return CustomersHttp.put(
    `${PREFIX}/update-customer-company-data/${customerCompanyToUpdateId}`,
    { ...payload },
    {
      headers: {
        ...headers,
        Authorization: `Bearer ${accessToken}`
      }
    }
  )
}

export const rejectCustomerCompanyFunding = (customerCompanyFundingId: string, payload: object, accessToken: string) => {
  return CustomersHttp.put(`${PREFIX}/reject-customer-company-funding/${customerCompanyFundingId}`, payload, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const revertCustomerCompanyFunding = (customerCompanyFundingId: string, accessToken: string) => {
  return CustomersHttp.put(
    `${PREFIX}/revert-customer-company-funding/${customerCompanyFundingId}`,
    {},
    {
      headers: {
        ...headers,
        Authorization: `Bearer ${accessToken}`
      }
    }
  )
}

export const approveCustomerCompanyFunding = (customerCompanyFundingId: string, selectedCountry: string, accessToken: string) => {
  return CustomersHttp.put(
    `${PREFIX}/approve-customer-company-funding/${customerCompanyFundingId}/${selectedCountry}`,
    {},
    {
      headers: {
        ...headers,
        Authorization: `Bearer ${accessToken}`
      }
    }
  )
}

export const getCustomerCompanyPockets = (customerCompanyId: string, currency: string, accessToken: string) => {
  return CustomersHttp.get(`${PREFIX}/customer-company-pockets/${customerCompanyId}/${currency}`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getCustomerCompanyComplianceUsers = (customerCompanyId: string, accessToken: string) => {
  return CustomersHttp.get(`${PREFIX}/customer-company-compliance-users/${customerCompanyId}`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const createCustomerCompanyPocket = (payload: object, accessToken: string) => {
  return CustomersHttp.post(`${PREFIX}/create-customer-company-pocket`, payload, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const deleteCustomerCompanyPocket = (payload: object, accessToken: string) => {
  return CustomersHttp.post(`${PREFIX}/delete-customer-company-pocket`, payload, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getCustomerCompanyMoneyMovementsBySearch = (payload: object, accessToken: string) => {
  return CustomersHttp.post(`${PREFIX}/customer-company-money-movements-by-search`, payload, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getCustomerCompanyMoneyMovements = (customerCompanyId: string, accessToken: string) => {
  return CustomersHttp.get(`${PREFIX}/customer-company-money-movements/${customerCompanyId}`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getCustomerCompanies = (accessToken: string) => {
  return CustomersHttp.get(`${PREFIX}/customer-companies`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const syncCompanyPocketsData = (payload: object, accessToken: string) => {
  return CustomersHttp.post(`${PREFIX}/sync-company-pockets-data`, payload, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getDeletedCustomerCompanyPockets = (customerCompanyId: string, accessToken: string) => {
  return CustomersHttp.get(`${PREFIX}/deleted-customer-company-pockets/${customerCompanyId}`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getPocketsTransfers = (accessToken: string) => {
  return CustomersHttp.get(`${PREFIX}/pockets-transfers`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getPocketsTransfersById = (pocketTransferId: string, accessToken: string) => {
  return CustomersHttp.get(`${PREFIX}/pockets-transfers-by-id/${pocketTransferId}`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getPocketsTransfersByCustomerCompany = (customerCompanyId: string, selectedCountry: string, pocketTransferId: string, accessToken: string) => {
  return CustomersHttp.get(`${PREFIX}/pockets-transfers/${customerCompanyId}/${pocketTransferId}/${selectedCountry}`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getPocketCurrentBalance = (pocketId: string, accessToken: string) => {
  return CustomersHttp.get(`${PREFIX}/pocket-current-balance/${pocketId}`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const saveConnectionIP = (ipData: string, userAgent: string, accessToken: string) => {
  return CustomersHttp.post(
    `${PREFIX}/save-connection-ip`,
    { ipData, userAgent },
    {
      headers: {
        ...headers,
        Authorization: `Bearer ${accessToken}`
      }
    }
  )
}

export const saveTransferBetweenPockets = (payload: object, accessToken: string) => {
  return CustomersHttp.post(`${PREFIX}/save-transfer-bewteen-pockets`, payload, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}
